import dynamic from 'next/dynamic';
import { ReactElement, memo, useState, useEffect } from 'react';
import * as S from './VisitorsGuide.styles';
import { TPublicBlogs } from '@/entities/Blogs/domain/Blogs.domain';
import { ICategoriesItem, ICategory } from '@/common/service/api/Categories/Categories.domain';
import TopAttractions from '@/shared/TopAttractions/ui/TopAttractions';
import Layout from '@/shared/Layout/ui/Layout';
import { IDestination, IDestinationList } from '@/entities/Destination/domain/Destination.domain';
import { VisitorsGuideProps } from '@/common/service/api/Destination/Destination.domain';
import Paragraph from '@/shared/Paragraph/ui/Paragraph';
import Subscribe from '@/shared/Subscribe/ui/Subscribe';
import { useMedia } from '@/common/service/hooks/useMedia';
import { DropdownLink } from '@/common/ui/inputs/DropdownLink';
import { Container } from '@/common/ui/containers/Container/Container';
import VisitorsGuideMeta from '../../app/VisitorsGuideMeta';
import { IRecommendsItem } from '@/shared/FeaturedActivities/ui/featuredActivitiesPreview/FeaturedActivitiesPreview.types';
import { TFaqItem } from '@/common/service/api/FAQ/FAQ.domain';
import FAQ from '@/shared/FAQ/ui/FAQ';
import { VisitorsGuideBlogsProps } from './VisitorsGuideBlogs';
import { removeDuplicate } from '@/common/app/utils/arrayUtils';
import { TLandmarksFull } from '@/screens/LandmarkMainPage/domain/Landmarks.domain';
import { VisitorsGuideLandmarksProps } from './VisitorsGuideLandmarks';
import { IBreadcrumbsItem } from '@/shared/Breadcrumbs/domain/Breadcrumbs.domain';
import { NewFeaturedActivities } from '@/shared/FeaturedActivities/ui/NewFeaturedActivities';

const VisitorsGuideLandmarks = dynamic<VisitorsGuideLandmarksProps>(
    () => import('./VisitorsGuideLandmarks').then((module) => module.VisitorsGuideLandmarks),
    {
        ssr: false,
    }
);

const VisitorsGuideBlogs = dynamic<VisitorsGuideBlogsProps>(
    () => import('./VisitorsGuideBlogs').then((module) => module.VisitorsGuideBlogs),
    {
        ssr: false,
    }
);

export type VisitorsGuideTypes = {
    blogs: TPublicBlogs;
    landmarks: TLandmarksFull;
    featuredCategories: ICategoriesItem[];
    pageDestination: VisitorsGuideProps & IDestination & IDestinationList;
    categories: ICategory[];
    destination: IDestination;
    images: string[];
    faq: TFaqItem[];
    breadcrumbs: IBreadcrumbsItem[];
    recommendedTrips: IRecommendsItem[];
};
type InitialCategoryProps = {
    name: string;
    href: string;
    subtitle: string;
};

const VisitorsGuideContainer = ({
    blogs,
    landmarks,
    destination,
    categories: filteredCategories,
    featuredCategories,
    pageDestination,
    images,
    faq,
    breadcrumbs,
    recommendedTrips,
}: VisitorsGuideTypes): ReactElement => {
    const title = `${pageDestination.name}, ${pageDestination.state.name}`;
    const isMobileView = useMedia('(max-width: 991px)');
    const [categories, setCategories] = useState<InitialCategoryProps[]>([]);
    const knownFor = pageDestination.known_for;

    const item_list_id = typeof window !== 'undefined' ? window.location.pathname : '';

    useEffect(() => {
        filteredCategories?.map((cat) => {
            const allCategory = {
                name: 'All Activities',
                href: `/destination/${destination.state.code}/${destination.slug}/things-to-do/`.toLowerCase(),
                subtitle: '',
            };
            const category = {
                name: cat.name,
                href: `/destination/${destination.state.code}/${destination.slug}/things-to-do/${cat.slug}/`.toLowerCase(),
                subtitle: '',
            };

            setCategories((cate) => removeDuplicate([allCategory, ...cate, category], 'name'));
        });
    }, [destination.slug, filteredCategories, destination.state.code]);

    return (
        <Layout
            TitleName={title}
            images={images}
            totalActivities={pageDestination.total_activities}
            breadcrumbs={breadcrumbs}
            withMobileHeroImage
        >
            <VisitorsGuideMeta pageDestination={pageDestination} />
            <S.PageWrapper>
                {pageDestination.visitor_guide.short.length > 5 && (
                    <S.ShortParagraph data-test-id="Description">
                        <Paragraph
                            id="ts_paragraph_short"
                            as="div"
                            innerHtml={
                                pageDestination.visitor_guide.short === 'null'
                                    ? ''
                                    : pageDestination.visitor_guide.short
                            }
                        />
                    </S.ShortParagraph>
                )}

                <NewFeaturedActivities
                    defaultActivities={recommendedTrips || []}
                    title={`Featured Activities in ${pageDestination.name}`}
                    item_list_id={item_list_id}
                    item_list_name={`Featured Activities in ${pageDestination.name}`}
                    placeholders={{}}
                />

                <Container>
                    <S.DescriptionTitle>
                        {pageDestination.name}, {pageDestination.state.name}, Visitors Guide
                    </S.DescriptionTitle>
                    <Paragraph
                        id="ts_paragraph_full"
                        as="div"
                        innerHtml={
                            pageDestination.visitor_guide.full === 'null'
                                ? ''
                                : pageDestination.visitor_guide.full
                        }
                    />
                </Container>
                <Container>
                    <S.WrapperDescription>
                        <S.DescriptionTitle data-test-id="Description_title">
                            What is {pageDestination.name} known for
                        </S.DescriptionTitle>
                        <Paragraph id="ts_paragraph_full" as="div" innerHtml={knownFor} />
                    </S.WrapperDescription>
                </Container>
                {Boolean(featuredCategories?.length) && (
                    <S.WrapperTopAttractions>
                        <TopAttractions
                            featuredCategories={featuredCategories ?? []}
                            destinationName={pageDestination.name}
                        />
                    </S.WrapperTopAttractions>
                )}
                {pageDestination.content_only !== '1' && (
                    <Container>
                        <S.CategorySelectWrapper data-test-id="Category_selector">
                            <S.SelectWrapper>
                                <S.Title>
                                    {/* {pageDestination.name}  */}
                                    Let&apos;s Get Started
                                </S.Title>
                                <S.Dropdown>
                                    <DropdownLink
                                        list={categories}
                                        title={'Select Your Category'}
                                    />
                                </S.Dropdown>
                            </S.SelectWrapper>
                        </S.CategorySelectWrapper>
                    </Container>
                )}

                <VisitorsGuideBlogs
                    blogs={blogs}
                    isMobileView={isMobileView}
                    destinationId={pageDestination.id}
                    title={`${pageDestination.name} News`}
                    subtitle="Keeping you informed"
                    btnTitle="View More Blogs"
                />
                <S.WrapperSubscribe>
                    <Subscribe />
                </S.WrapperSubscribe>

                <VisitorsGuideLandmarks
                    landmarks={landmarks}
                    isMobileView={isMobileView}
                    pageDestination={pageDestination}
                />

                {Boolean(faq?.length) && <FAQ data={faq} />}
            </S.PageWrapper>
        </Layout>
    );
};

export const VisitorsGuide = memo(VisitorsGuideContainer);
export default VisitorsGuide;
